type RuntimeConfig = {
  DIGIOBCAN_API_URL: string;
  VITE_CORE_API_URL: string;
  CONSENT_API_URL: string;
  AUTH_SERVER: string;
  MQTT_URI: string;
  VITE_LOGGER_API_URL: string;
  RECAPTCHA_KEY: string;
  VAPID_PUBLIC_KEY: string;
};

declare global {
  interface Window {
    __CONFIG__?: Partial<RuntimeConfig>;
  }
}

const RUNTIME_KEYS: (keyof RuntimeConfig)[] = [
  'DIGIOBCAN_API_URL',
  'VITE_CORE_API_URL',
  'CONSENT_API_URL',
  'AUTH_SERVER',
  'MQTT_URI',
  'VITE_LOGGER_API_URL',
  'RECAPTCHA_KEY',
  'VAPID_PUBLIC_KEY',
];

const runtime = (window.__CONFIG__ ?? {}) as Partial<RuntimeConfig>;
for (const key of RUNTIME_KEYS) {
  if (runtime[key] === undefined) {
    throw new Error(
      `window.__CONFIG__ is missing "${key}" - is /config.js served?`,
    );
  }
}

export const config = {
  CLIENT_ID: 'digiobcan' as const,
  ...(runtime as RuntimeConfig),
  DEFAULT_TAB_QUERY_PARAM_NAME: 'tab',
  // `[lng, lat]`, nie `number[]`: každý, kto ju používa, ju rozoberá na dvojicu
  // a bez toho typu si ju musel docastovať sám.
  DEFAULT_POSITION: [21.239311, 49.001831] as [number, number],
  URN_PREFIX: 'urn:ngsi-ld:presov.sk:digital-obcan',
  API_URN_PREFIXES: {
    DigiobcanApi: 'urn:ngsi-ld:presov.sk:digital-obcan',
    CoreApi: 'urn:ngsi-ld:presov.sk',
    LoggerApi: 'urn:ngsi-ld:presov.sk',
    ConsentApi: 'urn:ngsi-ld:presov.sk:Consents',
  },
  LOCAL_STORAGE: {
    // `feed_filters` (tri nezávislé zaškrtávacie filtre) zanikol s redizajnom
    // hlavnej stránky - nahradil ho jeden segment. Kľúč sa zámerne nerecykluje:
    // v prehliadačoch používateľov leží pod starým menom objekt iného tvaru.
    FEED_SEGMENT: 'feed_segment',
    FEED_SORT_ORDER: 'feed_sort_order',
    // Zaškrtnuté bunky štatistiky na hlavnej stránke. Vedľa segmentu a radenia,
    // teda tam, kde sú ostatné voľby panela - a nie v adrese: je to nastavenie
    // používateľa, nie stav, na ktorý sa posiela odkaz.
    FEED_STAT_FILTERS: 'feed_stat_filters',
  },
  PAGE_TITLE: 'Môj Prešov',
  CONSENTS: {
    // empty id => ConsentModal is not shown
    id: 'user-location',
  },
  PLUS_CODES_BASE_URL: `https://maps.google.com/maps/search`,
  // Dočasne skryté sekcie aplikácie. Prepnutím na true sa vrátia do navigácie
  // aj do routovania v src/App.tsx - inde nič meniť netreba.
  CUSTOM_FEATURES: {
    BENEFITS: false,
    PROJECTS: false,
  },
  CUSTOM_LIMIT: {
    // Musí sedieť s client_max_body_size na nginxe/ingresse pre moj.presov.sk.
    // Ak je tu viac než na serveri, upload spadne na 413 až po nahratí celého súboru.
    MEDIA_UPLOAD_MB: 250,
    PROJECTS: 25,
    FEED: {
      NEWS: 25,
      USER_REPORTS: 25,
      PUBLIC_REPORTS: 100,
    },
    ADMIN: {
      REPORTS: 25,
    },
  },
  // Podkladová mapa ArcGIS. 'gray-vector' = Light Gray Canvas (sivá),
  // 'streets-navigation-vector' = pôvodná farebná navigačná mapa.
  // Prepínač vpravo dole prepína na satelit ('hybrid') nezávisle od tejto hodnoty.
  CUSTOM_MAP_BASEMAP: 'gray-vector',
  // `CUSTOM_COLOR` tu bolo a je preč: farby značiek na mape sú farby stavov
  // a tie patria téme (`FEED_COLOR` vo `feedStatus.ts`), nie konfigurácii.
  // Kým to boli dve sady, legenda pod mapou a guličky na nej hovorili o tom
  // istom stave inou farbou.
};
